import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollectionHandle } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { DateTimeAspect } from '@adornis/filter/aspect-schemas/DateTimeAspect.js';
import { DoesNotExistAspect } from '@adornis/filter/aspect-schemas/ExistsAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { TeamMeeting } from '@chancenwerk/shared/attendance/entities/TeamMeeting.js';
import { Course } from '@chancenwerk/shared/courses/Course.js';
import { ChancenwerkUserFilter } from '@chancenwerk/shared/filter/ChancenwerkUserFilter.js';
import { workTrackingCreatedEmail } from '@chancenwerk/shared/mailer/mailTemplates.js';
import { chancenWerkMailer } from '@chancenwerk/shared/mailer/server/mailer.js';
import { KPF } from '@chancenwerk/shared/master-data-management/kpf/entities/KPF.js';
import { UserAPI } from '@chancenwerk/shared/master-data-management/users/api/UserAPI.js';
import type { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import { Mitarbeiter } from '@chancenwerk/shared/master-data-management/users/entities/Mitarbeiter.js';
import { PresenceRecord, SchuKoPresenceRecord, Status } from '@chancenwerk/shared/PresenceRecord.js';
import { KPFPublicInformationAPI } from '@chancenwerk/shared/public-access/api/KPFPublicInformationAPI.js';
import { ChancenRoleAspect } from '@chancenwerk/shared/semantics/ChancenRoleAspect.js';
import { Role } from '@chancenwerk/shared/semantics/Role.js';
import { DateTime } from 'luxon';
import { catchError, firstValueFrom, from, of, switchMap } from 'rxjs';
import type { WorkTrackingStatusAspect } from '../entitites/aspects/WorkTrackingStatusAspect.js';
import {
  WorkTrackingAuslage,
  type WorkTrackingAuslagenTypes,
  WorkTrackingAuslagenTypeToKostenart,
} from '../entitites/WorkTrackingAuslage.js';
import { WorkTrackingEntry } from '../entitites/WorkTrackingEntry.js';
import {
  WorkTrackingSingleTaetigkeitenTypes,
  WorkTrackingTaetigkeit,
  WorkTrackingTaetigkeitenTypes,
  WorkTrackingTaetigkeitenTypeToKostenart,
} from '../entitites/WorkTrackingTaetigkeit.js';
import { WorkTrackingTaetigkeitenState } from '../entitites/WorkTrackingTaetigkeitenState.js';

const createWorkTrackingEntry = registerMutation({
  type: () => String,
  operationName: 'createWorkTrackingEntry',
  resolve: (userID: string, month: DateTime) => {
    return async () => {
      const myself = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!myself) {
        throw new Error('not logged in');
      }

      const alreadExists = await loadMongoData(WorkTrackingEntry, {
        userID,
        date: { $gte: month.startOf('month').toJSDate(), $lt: month.endOf('month').toJSDate() },
      });

      if (alreadExists.length) {
        return alreadExists[0]?._id ?? '';
      }

      const id = await new WorkTrackingEntry({ userID, date: month.toJSDate() }).create();

      await populateWorkTrackingEntry(id)();

      const populated = await getByID(WorkTrackingEntry, id)({ entryCount: 1 });

      // * Skip mail if no entries were populated
      if (populated?.entryCount === 0) {
        logger.info({ id, userID, month }, 'WorkTrackingEntry created but no entries populated. Skipping email');
        return;
      }

      // * Send email
      try {
        const user = (await loadMongoData(Mitarbeiter, userID, {
          projection: { email: 1, emailChancenwerk: 1, vorname: 1, nachname: 1 },
        })) as PartialEntityData<Mitarbeiter>;

        const msg = await Message.compose({
          subject: `Tätigkeitserfassung für Monat ${month.toFormat('MM')}. ist bereit`,
          html: workTrackingCreatedEmail({
            date: month,
            link: projectConfig.get('CAMPUS_URL') + 'work-tracking/' + id,
          }),
        });

        await chancenWerkMailer.sendMail(msg, new Recipient(user?.emailChancenwerk ?? user?.email ?? ''));
      } catch (e) {
        logger.warn(e, 'Error while sending mail');
      }

      return id;
    };
  },
  options: {},
  params: [
    { type: () => String, name: 'userID' },
    { type: () => DateTime, name: 'month' },
  ],
});

const populateWorkTrackingEntry = registerMutation({
  type: () => Boolean,
  operationName: 'populateWorkTrackingEntry',
  resolve: (entryID: string) => {
    return async () => {
      const myself = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!myself) {
        throw new Error('not logged in');
      }

      const exists = await loadMongoData(WorkTrackingEntry, entryID);
      if (!exists || !exists.date) {
        return false;
      }

      const user = await Mitarbeiter.getByID<Mitarbeiter>(exists.userID ?? '')({
        vertraege: {
          taetigkeiten: { predicateID: 1, objectID: 1, aufwandsentschaedigung: 1, startDate: 1, endDate: 1 },
        },
      });

      if (!user) {
        return new Error('user not found');
      }

      //
      // Populate from Attendance
      //

      const taetigkeiten = await loadMongoData(WorkTrackingTaetigkeit, {
        userID: exists.userID,
        date: {
          $gte: DateTime.fromJSDate(exists.date).startOf('month').toJSDate(),
          $lt: DateTime.fromJSDate(exists.date).endOf('month').toJSDate(),
        },
      });

      const toCreate: WorkTrackingTaetigkeit[] = [];

      const attendanceLeKo = await loadMongoData(
        PresenceRecord,
        {
          date: {
            $gte: DateTime.fromJSDate(exists.date).startOf('month').toJSDate(),
            $lt: DateTime.fromJSDate(exists.date).endOf('month').toJSDate(),
          },
          'lekoRecords.userID': exists.userID,
        },
        { projection: { _id: 1, lekoRecords: 1 } },
      );

      const courseIDs = [
        ...attendanceLeKo.reduce((courses: Set<string>, attendance) => {
          if (!attendance.courseID) {
            return courses;
          }
          courses.add(attendance.courseID);
          return courses;
        }, new Set<string>()),
      ];

      const courses = await Course.getByID<Course>(courseIDs)({ kpfID: 1, startTime: 1, endTime: 1 });

      attendanceLeKo.forEach(attendance => {
        if (taetigkeiten.find(t => t.generatedFromID === attendance._id)) {
          return;
        }

        const a = attendance.lekoRecords?.find(record => record.userID === exists.userID);
        if (a?.status === Status.PRESENT) {
          const t = user.findActiveTaetigkeitByEinsatzstelle(
            courses.find(c => c._id === attendance.courseID)?.kpfID ?? '',
          );

          toCreate.push(
            new WorkTrackingTaetigkeit({
              userID: exists.userID,
              date: attendance.date,
              generatedFromID: attendance._id,
              stateContractor: WorkTrackingTaetigkeitenState.OPEN,
              stateResponsible: WorkTrackingTaetigkeitenState.FINISHED,
              kpfID: courses.find(c => c._id === attendance.courseID)?.kpfID,
              startTime: courses.find(c => c._id === attendance.courseID)?.startTime?.toJSDate(),
              endTime: courses.find(c => c._id === attendance.courseID)?.endTime?.toJSDate(),
              type: WorkTrackingTaetigkeitenTypes.LEKO,
              money: t?.aufwandsentschaedigung ?? 0,
            }),
          );
        }
      });

      const attendanceSchuKo = await loadMongoData(
        SchuKoPresenceRecord,
        {
          date: {
            $gte: DateTime.fromJSDate(exists.date).startOf('month').toJSDate(),
            $lt: DateTime.fromJSDate(exists.date).endOf('month').toJSDate(),
          },
          'schukoRecords.userID': exists.userID,
        },
        { projection: { _id: 1, schukoRecords: 1 } },
      );

      attendanceSchuKo.forEach(attendance => {
        if (taetigkeiten.find(t => t.generatedFromID === attendance._id)) {
          return;
        }

        const a = attendance.schukoRecords?.find(record => record.userID === exists.userID);
        if (a?.status === Status.PRESENT) {
          const t = user.findActiveTaetigkeitByEinsatzstelle(
            courses.find(c => c._id === attendance.kpfID)?.kpfID ?? '',
          );

          toCreate.push(
            new WorkTrackingTaetigkeit({
              userID: exists.userID,
              date: attendance.date,
              generatedFromID: attendance._id,
              kpfID: attendance.kpfID,
              stateContractor: WorkTrackingTaetigkeitenState.OPEN,
              stateResponsible: WorkTrackingTaetigkeitenState.FINISHED,
              type: WorkTrackingTaetigkeitenTypes.SCHUKO,
              money: t?.aufwandsentschaedigung ?? 0,
            }),
          );
        }
      });

      //
      // Populate from TeamAttendance
      //

      const meetings = await loadMongoData(TeamMeeting, {
        'attendances.userID': user._id,
        when: {
          $gte: DateTime.fromJSDate(exists.date).startOf('month').toJSDate(),
          $lt: DateTime.fromJSDate(exists.date).endOf('month').toJSDate(),
        },
      });

      toCreate.push(
        ...(meetings
          .map(meeting => {
            if (taetigkeiten.find(t => t.generatedFromID === meeting._id)) {
              return null;
            }

            return new WorkTrackingTaetigkeit({
              userID: exists.userID,
              date: meeting.when,
              generatedFromID: meeting._id,
              kpfID: meeting.kpfId,
              stateContractor: WorkTrackingTaetigkeitenState.OPEN,
              stateResponsible: WorkTrackingTaetigkeitenState.FINISHED,
              type: WorkTrackingSingleTaetigkeitenTypes.TEAM_MEETING,
              money: 0,
            });
          })
          .filter(Boolean) as WorkTrackingTaetigkeit[]),
      );

      //
      // Create
      //

      for (const t of toCreate) {
        await t.create();
      }

      return true;
    };
  },
  options: {},
  params: [{ type: () => String, name: 'entryID' }],
});

const workTrackingInitMonth = registerMutation({
  type: () => Boolean,
  operationName: 'WorkTrackingAPI_initMonth',
  resolve: (date: DateTime) => {
    return async () => {
      const myself = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!myself) {
        throw new Error('not logged in');
      }

      const limit = 10;
      const initByFilter = async (filter: ChancenwerkUserFilter) => {
        let skip = 0;
        while (true || skip < 10000) {
          logger.info({ skip, limit }, '[WorkTracking] init month: fetching users');

          const res = (await firstValueFrom(
            UserAPI.subscribeByFilter(skip, limit, filter)({ _id: 1 }),
          )) as ChancenwerkUser[];

          await Promise.all(res.map(r => createWorkTrackingEntry(r._id, date)()));
          await new Promise(resolve => setTimeout(resolve, 3000));

          if (res.length < limit) {
            break;
          }
          skip += limit;
        }
      };

      await initByFilter(
        new ChancenwerkUserFilter({
          aspects: [
            new BooleanAspect({ customID: 'active', value: true }),
            new ChancenRoleAspect({ value: Role.SCHUKO }),
          ],
        }),
      );

      await initByFilter(
        new ChancenwerkUserFilter({
          aspects: [
            new BooleanAspect({ customID: 'active', value: true }),
            new ChancenRoleAspect({ value: Role.LEKO }),
          ],
        }),
      );

      return true;
    };
  },
  options: {},
  params: [{ type: () => DateTime, name: 'date' }],
});

const subscribeWorkTrackingEntries = registerSubscription({
  type: () => [WorkTrackingEntry],
  operationName: 'subscribeWorkTrackingEntries',
  resolve: (skip: number, limit: number, filter: AdornisFilter) => {
    return (gqlFields: BaseQLSelectionSet<WorkTrackingEntry>) => {
      const month = filter.getFilterAspect<StringAspect>('month');
      const year = filter.getFilterAspect<StringAspect>('year');
      const selector = {
        ...filter.getMongoFilter(),
        ...(month?.value && year?.value
          ? {
              date: {
                $gte: DateTime.fromFormat(`${year.value}-${month.value}-01`, 'yyyy-MM-dd').startOf('month').toJSDate(),
                $lte: DateTime.fromFormat(`${year.value}-${month.value}-01`, 'yyyy-MM-dd').endOf('month').toJSDate(),
              },
            }
          : {}),
      };
      const handle = getCollectionHandle<WorkTrackingEntry>(WorkTrackingEntry._collectionName);
      return from(publishFilter(WorkTrackingEntry._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, { skip, limit });
        }),
        switchMap(async docs => {
          const workTrackingStatus = filter.getFilterAspect<WorkTrackingStatusAspect>('status');
          if (!workTrackingStatus?.values) {
            return docs;
          }

          const resolved = await WorkTrackingEntry.getByID<WorkTrackingEntry>(docs.map(d => d._id))({
            _id: 1,
            userID: 1,
            date: 1,
            state: 1,
            stateContractor: 1,
            stateResponsible: 1,
          });

          return docs.filter(r => {
            const elem = resolved.find(e => e._id === r._id);
            if (elem?.state && workTrackingStatus.values?.includes(elem?.state)) {
              return true;
            }
            return false;
          });
        }),
        catchError(e => {
          return of([] as WorkTrackingEntry[]);
        }),
      );
    };
  },
  options: {},
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
});

const subscribeWorkTrackingTaetigkeiten = registerSubscription({
  type: () => [WorkTrackingTaetigkeit],
  operationName: 'subscribeWorkTrackingTaetigkeiten',
  resolve: (skip: number, limit: number, filter: AdornisFilter) => {
    return (gqlFields: BaseQLSelectionSet<WorkTrackingTaetigkeit>) => {
      const selector = { ...filter.getMongoFilter() };
      const handle = getCollectionHandle<WorkTrackingTaetigkeit>(WorkTrackingTaetigkeit._collectionName);
      return from(publishFilter(WorkTrackingTaetigkeit._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            sort: { date: 1 },
            skip,
            limit,
          });
        }),
        catchError(e => {
          return of([] as WorkTrackingTaetigkeit[]);
        }),
      );
    };
  },
  options: {},
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
});

const datevWorkTrackingExport = registerQuery({
  type: () => String,
  operationName: 'datevWorkTrackingExport',
  resolve: (month: DateTime) => {
    return async () => {
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user) {
        throw new Error('User not found');
      }

      const monthAspect = new DateTimeAspect({
        fieldName: 'date',
        mode: 'range',
        firstValue: month.startOf('month').toJSDate(),
        secondValue: month.endOf('month').toJSDate(),
      });
      const monthFilter = new AdornisFilter({ aspects: [monthAspect] });
      const entries = await firstValueFrom(
        subscribeWorkTrackingEntries(0, 10000, monthFilter)({ user: { _id: 1, vorname: 1, nachname: 1 } }),
      );

      logger.info({ month: month.toFormat('yyyy-MM'), entries: entries.length }, '[WorkTracking] datev export');

      const header = 'debitor;kostenstelle;betrag;type;type_kostenstelle';
      const lines: string[] = [];

      // * Loop through all entries
      for (const entry of entries) {
        logger.info(
          { userID: entry.user?._id, user: entry.user?.vorname + ' ' + entry.user?.nachname },
          '[WorkTracking] datev export',
        );

        // * Fetch all types of data
        const selectionSet = {
          money: 1,
          kpfID: 1,
          kpf: { kostenstellennummer: 1 },
          note: 1,
          type: 1,
        } as BaseQLSelectionSet<WorkTrackingAuslage | WorkTrackingTaetigkeit>;
        const singleTaetigkeiten = await firstValueFrom(
          subscribeWorkTrackingTaetigkeiten(
            0,
            1000,
            new AdornisFilter({
              aspects: [
                new BooleanAspect({ fieldName: 'isSingle', value: true }),
                new StringAspect({ fieldName: 'userID', value: entry.user?._id }),
                monthAspect,
              ],
            }),
          )(selectionSet),
        );
        const taetigkeiten = await firstValueFrom(
          subscribeWorkTrackingTaetigkeiten(
            0,
            1000,
            new AdornisFilter({
              aspects: [
                new DoesNotExistAspect({ fieldName: 'isSingle' }),
                new StringAspect({ fieldName: 'userID', value: entry.user?._id }),
                monthAspect,
              ],
            }),
          )(selectionSet),
        );
        const auslagen = await firstValueFrom(
          subscribeWorkTrackingAuslagen(
            0,
            1000,
            new AdornisFilter({
              aspects: [new StringAspect({ fieldName: 'userID', value: entry.user?._id }), monthAspect],
            }),
          )(selectionSet),
        );

        logger.info(
          {
            entry: entry._id,
            singleTaetigkeiten: singleTaetigkeiten.length,
            taetigkeiten: taetigkeiten.length,
            auslagen: auslagen.length,
          },
          '[WorkTracking] datev export',
        );

        // * Fetch all KPFIDs
        const kpfIDs = [
          ...new Set([
            ...singleTaetigkeiten.map(t => t.kpfID),
            ...taetigkeiten.map(t => t.kpfID),
            ...auslagen.map(t => t.kpfID),
          ]),
        ];

        for (const kpfID of kpfIDs) {
          const filtered = {
            taetigkeiten: taetigkeiten.filter(t => t.kpfID === kpfID),
            singleTaetigkeiten: singleTaetigkeiten.filter(t => t.kpfID === kpfID),
            auslagen: auslagen.filter(t => t.kpfID === kpfID),
            kostenstelle: taetigkeiten.find(t => t.kpfID === kpfID)?.kpf?.kostenstellennummer ?? '',
          };

          const typeCounts: Record<string, number> = {};
          filtered.taetigkeiten.forEach(t => {
            typeCounts[t.type ?? ''] ??= 0;
            // @ts-expect-error
            typeCounts[t.type ?? ''] += 1;
          });

          const highestType = Object.entries(typeCounts).reduce((a, b) => (a[1] > b[1] ? a : b), ['', 0])[0];

          for (const type of Object.keys(typeCounts)) {
            let amount = filtered.taetigkeiten.filter(t => t.type === type).reduce((sum, t) => sum + (t.money ?? 0), 0);

            // * If the type is the highest type, add the other taetigkeiten
            if (type === highestType) {
              amount += filtered.singleTaetigkeiten.reduce((sum, t) => sum + (t.money ?? 0), 0);
            }

            lines.push(
              `${entry.user?.vorname} ${entry.user?.nachname};${
                filtered.kostenstelle
              };${amount};${type};${WorkTrackingTaetigkeitenTypeToKostenart(type as WorkTrackingTaetigkeitenTypes)}`,
            );
          }
        }

        return [
          header,
          ...lines,
          auslagen.map(
            a =>
              `${entry.user?.vorname} ${entry.user?.nachname};${
                taetigkeiten.find(t => t.kpfID === a.kpfID)?.kpf?.kostenstellennummer ?? ''
              };${a.money};${a.type};${WorkTrackingAuslagenTypeToKostenart(a.type as WorkTrackingAuslagenTypes)}`,
          ),
        ].join('\n');
      }
    };
  },
  options: {},
  params: [{ type: () => DateTime, name: 'month' }],
});

const subscribeWorkTrackingAuslagen = registerSubscription({
  type: () => [WorkTrackingAuslage],
  operationName: 'subscribeWorkTrackingAuslagen',
  resolve: (skip: number, limit: number, filter: AdornisFilter) => {
    return (gqlFields: BaseQLSelectionSet<WorkTrackingAuslage>) => {
      const selector = { ...filter.getMongoFilter() };
      const handle = getCollectionHandle<WorkTrackingAuslage>(WorkTrackingAuslage._collectionName);
      return from(publishFilter(WorkTrackingAuslage._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, { skip, limit });
        }),
        catchError(e => {
          return of([] as WorkTrackingAuslage[]);
        }),
      );
    };
  },
  options: {},
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
});

const subscribeSearchWorkTrackingKPF = registerSubscription({
  type: () => [KPF],
  operationName: 'subscribeSearchWorkTrackingKPF',
  resolve: (skip: number, limit: number, filter?: AdornisFilter) => {
    return (gqlFields: BaseQLSelectionSet<KPF>) => {
      return from(
        KPFPublicInformationAPI.getAll()({ kuerzel: 1, produkt: { kuerzel: 1 }, school: { kuerzel: 1, name: 1 } }).then(
          async res => {
            console.log(filter?.toJSON());
            console.log(filter?.search);
            return res.filter(kpf => {
              const allowedProducts = [
                'ProductLKK',
                'ProductLUS',
                'ProductSCH',
                'ProductSFZ',
                'ProductGTA',
                'ProductOGT',
                'ProductKGF',
                'ProductABS',
              ];
              if (!kpf.produkt?._class || !allowedProducts.includes(kpf.produkt._class)) {
                return false;
              }
              if (!filter?.search) {
                return true;
              }
              return (
                kpf.kuerzel.toLowerCase().includes(filter.search.toLowerCase()) ||
                kpf.school?.name?.toLowerCase().includes(filter.search.toLowerCase())
              );
            });
          },
        ),
      );
    };
  },
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => AdornisFilter, name: 'filter' },
  ],
});

export namespace WorkTrackingAPI {
  export const createEntry = createWorkTrackingEntry;
  export const populateEntry = populateWorkTrackingEntry;
  export const subscribeEntries = subscribeWorkTrackingEntries;
  export const subscribeTaetigkeiten = subscribeWorkTrackingTaetigkeiten;
  export const subscribeAuslagen = subscribeWorkTrackingAuslagen;
  export const initMonth = workTrackingInitMonth;
  export const datevExport = datevWorkTrackingExport;
  export const searchKPF = subscribeSearchWorkTrackingKPF;
}
